/**
 * AVBP Intake MODULE LIBRARY (descriptor layer) — built ONCE, selected per service.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  THE OWNER'S DIRECTION (honoured here):
 *    "DO NOT build 11 separate forms. Build a module library ONCE (each intake
 *     module = one reusable component; most modules are shared across services).
 *     Each service is then just a SELECTION of modules + per-field flags."
 *
 *  This file is the PURE descriptor layer of that library: it names every
 *  intake module by its spine `module_registry` key, records which group it
 *  belongs to, whether the implementation is REAL or a SCAFFOLD, and (for real
 *  modules) which `IntakeForm` fields it owns + how it validates. The matching
 *  React renderer is `components/IntakeModule.tsx`; the per-service selection is
 *  resolved through `lib/intakeCatalog.ts`. NONE of these three hardcodes a
 *  service — a service is a SELECTION of these module keys.
 *
 *  REAL vs SCAFFOLD (logged explicitly — no silent stubs):
 *    • REAL    — fully wired to the locked `answers` contract via intake.ts
 *                builders (identity, people/birth, concerns, goals, occupant,
 *                next-steps/timeline, consent). These render + persist + feed
 *                the report engine exactly as before.
 *    • SCAFFOLD — a minimal real capture (one or more fields) for a registry
 *                module that is `gap`/`designed`/under-specified in the spine.
 *                It renders behind the SAME selection mechanism and PERSISTS,
 *                but to a sibling `intake_extras.{key}` field — NEVER into the
 *                locked `answers` map — so the composer-read contract stays
 *                byte-identical and verbatim preservation holds. No engine
 *                wiring. Promote a scaffold → real by giving it owned `answers`
 *                fields + an intake.ts builder, with zero catalog change.
 *
 *  This layer is Firebase-free and React-free so the Node `.cjs` test runner
 *  exercises module resolution + per-service selection + validation purely.
 * ─────────────────────────────────────────────────────────────────────────
 */

import type { IntakeForm } from './intake';
import {
  validateIntakeForm,
  validateL1AIdentity,
  validateL1AGoal,
  validateL1AProperty,
  validateL1APeople,
  validateL1BIdentity,
  validateL1BProperty,
  validateL1BPlans,
  validateDrawingsUpload,
} from './intake';

/** A per-field flag from the spine `tier_templates.{tier}.modules`. */
export type ModuleFlag = 'mandatory' | 'on' | 'off';

/** How a module is implemented today (logged, never silent). */
export type ModuleImpl = 'real' | 'scaffold';

/**
 * The spine `module_registry` groups. Drives default stage grouping in the
 * catalog adapter (one stage per group, in this canonical order).
 */
export type ModuleGroup =
  | 'core'
  | 'person'
  | 'property_technical'
  | 'diagnostic'
  | 'design'
  | 'health'
  | 'staged'
  | 'ancillary';

/** Canonical group order — the default stage order when grouping by group. */
export const GROUP_ORDER: ModuleGroup[] = [
  'core',
  'person',
  'property_technical',
  'diagnostic',
  'design',
  'health',
  'staged',
  'ancillary',
];

/** Human title shown when a whole stage is named after a group. */
export const GROUP_TITLE: Record<ModuleGroup, string> = {
  core: 'About you & the property',
  person: 'People',
  property_technical: 'Property details',
  diagnostic: 'Background & readiness',
  design: 'Design brief',
  health: 'Health & wellness',
  staged: 'Follow-up',
  ancillary: 'Your request',
};

/**
 * One module descriptor. The renderer (`IntakeModule.tsx`) switches on `key`;
 * everything else here is metadata the catalog + engine read.
 */
export interface ModuleDef {
  /** spine module_registry key — the stable selection id. */
  key: string;
  /** spine group — drives default stage grouping. */
  group: ModuleGroup;
  /** Client-facing label for the module's own sub-section header. */
  label: string;
  /** One-line helper under the label. */
  blurb?: string;
  /** REAL (answers-wired) or SCAFFOLD (extras-only). */
  impl: ModuleImpl;
  /**
   * For REAL modules: the IntakeForm fields this module reads/writes. Purely
   * documentary today (the renderer owns the inputs) but lets the engine/tests
   * reason about which module owns a field. Empty for scaffolds (they own an
   * `intake_extras.{key}` slot instead).
   */
  formFields?: (keyof IntakeForm)[];
  /**
   * Scaffold capture descriptor — the minimal field(s) a scaffold renders.
   * Persisted under `intake_extras.{key}`. Ignored for real modules.
   */
  scaffoldFields?: ScaffoldField[];
  /**
   * Optional validator run when the module is MANDATORY in the active service.
   * Returns human messages ([] == ok). Receives the whole form so a module can
   * validate just its slice. Real modules only; scaffolds never block.
   */
  validate?: (form: IntakeForm) => string[];
}

/** A single scaffold capture input. */
export interface ScaffoldField {
  /** key under intake_extras.{moduleKey}.{name} */
  name: string;
  label: string;
  kind: 'text' | 'textarea' | 'select' | 'date' | 'checkbox';
  options?: string[];
  hint?: string;
}

/* ───────────────────────────── the library ───────────────────────────── */
/*
 * Every spine module_registry key appears here exactly once, so a service can
 * select ANY of them. REAL where we have a wired implementation; SCAFFOLD for
 * the gap/designed/under-specified registry modules (each still renders +
 * persists, behind the same selection mechanism).
 */

export const MODULE_LIBRARY: ModuleDef[] = [
  /* ── core (REAL) ── */
  {
    key: 'identity_property',
    group: 'core',
    label: 'Property & identity',
    blurb: 'About the property and where the report is going.',
    impl: 'real',
    formFields: [
      'client_name',
      'firm',
      'property_address',
      'project_type',
      'country',
      'scope_label',
      'language',
    ],
    // The identity module owns the truly-required client fields.
    validate: (form) => validateIntakeForm(form),
  },
  {
    key: 'consent_permissions',
    group: 'core',
    label: 'Consent & privacy',
    blurb: 'A one-minute read before we begin. Required to continue.',
    // The consent module is special — rendered by ConsentStage, gated by the
    // engine's consent path. Marked REAL (it persists the acceptance map).
    impl: 'real',
  },
  {
    key: 'presenting_concerns',
    group: 'core',
    label: "What's on your mind?",
    blurb: "The issues you'd like the analysis to address.",
    impl: 'real',
    formFields: ['presenting_concerns'],
  },
  {
    key: 'goals_aspirations',
    group: 'core',
    label: 'Goals',
    blurb: 'What outcomes would success look like?',
    impl: 'real',
    formFields: ['goals'],
  },
  {
    key: 'occupant_profile',
    group: 'core',
    label: 'Who uses this space',
    blurb: 'A short note on how the space is used day to day.',
    impl: 'scaffold',
    scaffoldFields: [
      {
        name: 'usage_note',
        label: 'How is this space used?',
        kind: 'textarea',
        hint: 'e.g. family home, owner-occupied shop, rented office',
      },
    ],
  },

  /* ── L1A redesign (2026-06-21) — the 5 redesigned L1A modules ──
   * These are L1A-SPECIFIC renderers built for "The Selection Path · Disha". They
   * own NEW IntakeForm fields (email/whatsapp/address/intended_use/properties +
   * the two-aspect concerns + the redesigned People birth capture). They are
   * separate keys from the L2B `identity_property`/`presenting_concerns`/
   * `person_birth_data` so L2B (and every other tier) is untouched; build the
   * shared modules cleanly here so later tiers can adopt them.
   */
  {
    key: 'l1a_about_you',
    group: 'core',
    label: 'About you',
    blurb: 'Your details and where the report is going.',
    impl: 'real',
    formFields: ['client_name', 'firm', 'email', 'whatsapp', 'address', 'country', 'language'],
    validate: (form) => validateL1AIdentity(form),
  },
  {
    key: 'l1a_goal_use',
    group: 'core',
    label: 'Your goal & intended use',
    blurb: 'What are you selecting a property for?',
    impl: 'real',
    formFields: ['intended_use', 'goals'],
    validate: (form) => validateL1AGoal(form),
  },
  {
    key: 'l1a_concerns',
    group: 'core',
    label: "What's on your mind?",
    blurb: 'The areas you want the analysis to focus on — issue and/or aspiration.',
    impl: 'real',
    formFields: ['presenting_concerns'],
  },
  {
    key: 'l1a_property_details',
    group: 'property_technical',
    label: 'Property details',
    blurb: 'The candidate properties you are comparing.',
    impl: 'real',
    formFields: ['properties'],
    validate: (form) => validateL1AProperty(form),
  },
  {
    key: 'l1a_people',
    group: 'person',
    label: 'People',
    blurb: 'Who uses the space — with per-person astrology.',
    impl: 'real',
    formFields: ['persons'],
    validate: (form) => validateL1APeople(form),
  },

  /* ── L1B redesign (2026-06-23) — "The Clarity Path · Darshan" ──
   * L1B reuses the L1A engine; these keys point IntakeModule.tsx at the L1A
   * renderers in L1B mode (single-property drawing-gate property details ·
   * willingness plans). They own the SAME shared IntakeForm fields, so cross-tier
   * answers carry over. The People stage uses the SAME `l1a_people` (incl. the
   * standard per-person health field).
   *
   * NOTE (owner ruling 2026-06-23): L1B / L1C / L2C "About you" now uses the L1A
   * about-you module (`l1a_about_you`) VERBATIM — the L1A About-you is complete for
   * every level and carries NO property-address field (the site location/address
   * lives in the Property details / Drawings stage). The `l1b_about_you` module
   * below (with the extra property-address field) is therefore currently
   * UNREFERENCED by any tier — kept (not deleted) so it remains available if a
   * future tier needs the property-address-in-about-you variant.
   */
  {
    key: 'l1b_about_you',
    group: 'core',
    label: 'About you & contact',
    blurb: 'Your details, where the report goes, and the property address.',
    impl: 'real',
    formFields: ['client_name', 'firm', 'email', 'whatsapp', 'address', 'country', 'language', 'property_address'],
    validate: (form) => validateL1BIdentity(form),
  },
  {
    key: 'l1b_property_details',
    group: 'property_technical',
    label: 'Property details',
    blurb: 'The single space you occupy — with the drawing gate.',
    impl: 'real',
    formFields: ['properties'],
    validate: (form) => validateL1BProperty(form),
  },
  {
    key: 'l1b_plans_preferences',
    group: 'diagnostic',
    label: 'Your plans & preferences',
    blurb: 'What you have tried + your willingness to act.',
    impl: 'real',
    formFields: ['prior_remedies_tried', 'willingness'],
    validate: (form) => validateL1BPlans(form),
  },

  /* ── person ── */
  {
    key: 'person_birth_data',
    group: 'person',
    label: 'People',
    blurb: 'Who lives or works here (birth details optional).',
    impl: 'real',
    formFields: ['persons', 'astro_in_scope'],
  },
  {
    key: 'family_map',
    group: 'person',
    label: 'Family / household map',
    blurb: 'How the people above relate to each other.',
    impl: 'scaffold',
    scaffoldFields: [
      {
        name: 'relationships_note',
        label: 'Relationships between the people listed',
        kind: 'textarea',
        hint: 'e.g. Rohit & Priya are spouses; Aarav is their son.',
      },
    ],
  },
  {
    key: 'astro_thread_remedy',
    group: 'person',
    label: 'Astrology thread (post-generation)',
    blurb: 'Captured after the report — left as a seam here.',
    impl: 'scaffold',
    scaffoldFields: [
      {
        name: 'astro_note',
        label: 'Any astrology-specific note',
        kind: 'textarea',
      },
    ],
  },

  /* ── property_technical ── */
  // The grid/entrances/AUO/north modules are STAFF-TECHNICAL — captured on the
  // SAME intake doc via the existing staff panel, NOT in this client flow. They
  // are registered here (so a service can select them) and rendered as a
  // staff-panel pointer scaffold in the client flow (they never block a client).
  {
    key: 'floor_geometry_grid',
    group: 'property_technical',
    label: 'Floor plan & grid',
    blurb: 'Captured by our team from your floor plan.',
    impl: 'scaffold',
    scaffoldFields: [
      {
        name: 'staff_note',
        label: 'Anything we should know about the floor plan',
        kind: 'textarea',
        hint: 'Our team traces the boundary and grid from your uploaded plan.',
      },
    ],
  },
  {
    key: 'entrances',
    group: 'property_technical',
    label: 'Entrances',
    blurb: 'Captured by our team on the grid.',
    impl: 'scaffold',
    scaffoldFields: [
      { name: 'entrance_note', label: 'Note on the main / other entrances', kind: 'textarea' },
    ],
  },
  {
    key: 'auo_fixtures',
    group: 'property_technical',
    label: 'Fixtures (AUO)',
    blurb: 'Captured by our team on the grid.',
    impl: 'scaffold',
    scaffoldFields: [
      { name: 'fixtures_note', label: 'Note on kitchen / water / toilets etc.', kind: 'textarea' },
    ],
  },
  {
    key: 'north_reference',
    group: 'property_technical',
    label: 'North direction',
    blurb: 'Captured by our team from your site location.',
    impl: 'scaffold',
    scaffoldFields: [
      { name: 'north_note', label: 'How you know the north (optional)', kind: 'text' },
    ],
  },
  {
    key: 'candidate_properties',
    group: 'property_technical',
    label: 'Candidate properties',
    blurb: 'The properties you are choosing between (selection services).',
    impl: 'scaffold',
    scaffoldFields: [
      {
        name: 'candidates_note',
        label: 'List the properties you are comparing',
        kind: 'textarea',
        hint: 'One per line — address or label. Our team sets each one up.',
      },
    ],
  },
  {
    key: 'property_media',
    group: 'property_technical',
    label: 'Photos & video',
    blurb: 'Upload happens in the portal media panel.',
    impl: 'scaffold',
    scaffoldFields: [
      { name: 'media_note', label: 'Note on the media you will share', kind: 'textarea' },
    ],
  },
  {
    key: 'space_survey',
    group: 'property_technical',
    label: 'Measured survey',
    blurb: 'A measured survey of the space (design services).',
    impl: 'scaffold',
    scaffoldFields: [
      {
        name: 'survey_status',
        label: 'Do you have a measured survey?',
        kind: 'select',
        options: ['Yes — I can share it', 'No — please arrange one', 'Not sure'],
      },
    ],
  },
  {
    key: 'property_drawings',
    group: 'property_technical',
    label: 'Existing drawings',
    blurb: 'Architectural / structural drawings you already have.',
    impl: 'scaffold',
    scaffoldFields: [
      {
        name: 'drawings_status',
        label: 'Which drawings do you have?',
        kind: 'textarea',
        hint: 'e.g. floor plans, elevations, structural, MEP',
      },
    ],
  },
  {
    key: 'property_constraints',
    group: 'property_technical',
    label: 'Site constraints',
    blurb: 'Boundaries, setbacks, slopes, anything fixed.',
    impl: 'scaffold',
    scaffoldFields: [
      { name: 'constraints_note', label: 'Known site constraints', kind: 'textarea' },
    ],
  },
  {
    // L1C / L2C (2026-06-23 v2) — REAL module. The architect's plan set the client
    // uploads for a Plan Review (L1C) or Integration coordination (L2C). Owns the
    // `drawings` form field: a PROPERTY block (full L1A site-location + true-north +
    // area, common to every version) + drawing VERSIONS (L1C = single set; L2C adds
    // revised versions over time). The grid is traced from these. Architect-contact
    // capture is the BRIGHT LINE: on L1C the architect fields are HIDDEN
    // (architectCoordination:false), on L2C shown — enforced in the renderer via the
    // per-tier flag (DrawingsUploadModule reads serviceTier).
    key: 'drawings_upload',
    group: 'property_technical',
    label: 'The property & your architect’s drawings',
    blurb: 'Site location & north (shared) + the architect’s plan set — we grid from these.',
    impl: 'real',
    formFields: ['drawings'],
    validate: (form) => validateDrawingsUpload(form),
  },

  /* ── diagnostic ── */
  {
    key: 'occupancy_history',
    group: 'diagnostic',
    label: 'History at this property',
    blurb: 'How long, and what has happened since you moved in.',
    impl: 'scaffold',
    scaffoldFields: [
      { name: 'since_when', label: 'Since when have you occupied this property?', kind: 'text' },
      {
        name: 'history_note',
        label: 'Anything notable since you moved in',
        kind: 'textarea',
        hint: 'Health, finances, relationships, disputes — in your words.',
      },
    ],
  },
  {
    key: 'prior_remedies_tried',
    group: 'diagnostic',
    label: 'Remedies already tried',
    blurb: 'Anything you have already done for this property.',
    impl: 'scaffold',
    scaffoldFields: [
      { name: 'prior_remedies', label: 'What have you already tried?', kind: 'textarea' },
    ],
  },
  {
    key: 'structural_willingness',
    group: 'diagnostic',
    label: 'Openness to structural change',
    blurb: 'How far you are willing to go with physical changes.',
    impl: 'scaffold',
    scaffoldFields: [
      {
        name: 'willingness',
        label: 'How open are you to structural changes?',
        kind: 'select',
        options: [
          'No structural change — remedies only',
          'Minor changes (move a door, a partition)',
          'Major changes if justified',
          'Open to anything',
        ],
      },
    ],
  },
  {
    key: 'implementation_timeline',
    group: 'diagnostic',
    label: 'Next steps',
    blurb: 'When you plan to begin remedies (optional).',
    impl: 'real',
    formFields: ['follow_up_start_date'],
  },
  {
    key: 'decision_maker_map',
    group: 'diagnostic',
    label: 'Who decides',
    blurb: 'Who signs off on changes to this property.',
    impl: 'scaffold',
    scaffoldFields: [
      { name: 'decision_makers', label: 'Who makes the final decisions?', kind: 'textarea' },
    ],
  },
  {
    key: 'attached_units',
    group: 'diagnostic',
    label: 'Attached units',
    blurb: 'Shared walls, adjoining shops/flats, common areas.',
    impl: 'scaffold',
    scaffoldFields: [
      { name: 'attached_note', label: 'Any attached / adjoining units?', kind: 'textarea' },
    ],
  },
  {
    key: 'use_constraints',
    group: 'diagnostic',
    label: 'Use constraints',
    blurb: 'Rules that limit what you can change (lease, society, law).',
    impl: 'scaffold',
    scaffoldFields: [
      { name: 'use_constraints_note', label: 'Any limits on how you can use/change it?', kind: 'textarea' },
    ],
  },
  {
    key: 'renovation_appetite',
    group: 'diagnostic',
    label: 'Renovation appetite',
    blurb: 'Budget / appetite for renovation work.',
    impl: 'scaffold',
    scaffoldFields: [
      {
        name: 'renovation_appetite',
        label: 'Appetite for renovation',
        kind: 'select',
        options: ['Minimal', 'Moderate', 'Significant', 'Full redesign'],
      },
    ],
  },

  /* ── design ── */
  {
    // AD-040 (2026-06-24) — now a REAL structured module (A→H superset). Owns the
    // `designProgramme` answers map (see lib/designProgramme.ts). Replaces the old
    // single "What spaces do you need?" textarea scaffold. Wired into every tier
    // that selects `design_programme` (L2C/L2A/L2D/L3A/L3B). H tier-depth deferred
    // — full depth rendered for now.
    key: 'design_programme',
    group: 'design',
    label: 'Design programme',
    blurb: 'The spaces you want, who it’s for, adjacencies, style, budget & constraints.',
    impl: 'real',
    formFields: ['designProgramme'],
  },
  {
    key: 'preferred_architectural_typology',
    group: 'design',
    label: 'Preferred style',
    blurb: 'The look / typology you have in mind.',
    impl: 'scaffold',
    scaffoldFields: [
      { name: 'typology', label: 'Preferred architectural style', kind: 'text' },
    ],
  },
  {
    key: 'architect_contact',
    group: 'design',
    label: 'Your architect',
    blurb: "Your architect's details, so we can coordinate.",
    impl: 'scaffold',
    scaffoldFields: [
      { name: 'architect_name', label: 'Architect name', kind: 'text' },
      { name: 'architect_contact', label: 'Architect phone / email', kind: 'text' },
    ],
  },
  {
    key: 'interior_designer_contact',
    group: 'design',
    label: 'Your interior designer',
    blurb: "Your interior designer's details (if any).",
    impl: 'scaffold',
    scaffoldFields: [
      { name: 'id_name', label: 'Interior designer name', kind: 'text' },
      { name: 'id_contact', label: 'Interior designer phone / email', kind: 'text' },
    ],
  },
  {
    key: 'occupancy_status',
    group: 'design',
    label: 'Occupancy status',
    blurb: 'Is the space occupied, vacant, or under construction?',
    impl: 'scaffold',
    scaffoldFields: [
      {
        name: 'occupancy_status',
        label: 'Current status of the space',
        kind: 'select',
        options: ['Occupied', 'Vacant', 'Under construction', 'Not yet built'],
      },
    ],
  },
  {
    // AD-042 (2026-06-25) — now a REAL structured module (11 groups) for L2D.
    // Owns `answers.constructionDetails` (see lib/constructionDetails.ts).
    key: 'construction_details',
    group: 'design',
    label: 'Construction details',
    blurb: 'Stage & structure, services, materials, soil, approvals — for detailing.',
    impl: 'real',
    formFields: ['constructionDetails'],
  },
  {
    // AD-042 — L3B "Plans & preferences" (design-path decision + preferences, 6
    // questions). REAL module owning `answers.designPlansPrefs`.
    key: 'l3b_plans_preferences',
    group: 'design',
    label: 'Plans & preferences',
    blurb: 'Your plan-in-mind, decision-maker, approval flow, references, non-negotiables & Vastu stance.',
    impl: 'real',
    formFields: ['designPlansPrefs'],
  },
  {
    key: 'site_visit_readiness',
    group: 'design',
    label: 'Site visit readiness',
    blurb: 'Is the site ready for a visit, and when.',
    impl: 'scaffold',
    scaffoldFields: [
      { name: 'site_visit_note', label: 'When can we visit the site?', kind: 'text' },
    ],
  },
  {
    key: 'map_availability',
    group: 'design',
    label: 'Map availability',
    blurb: 'Whether a measured / CAD map is available.',
    impl: 'scaffold',
    scaffoldFields: [
      {
        name: 'map_availability',
        label: 'Do you have a CAD / measured map?',
        kind: 'select',
        options: ['Yes (CAD/DWG)', 'Yes (PDF/image only)', 'No'],
      },
    ],
  },
  {
    key: 'prior_engagement_link',
    group: 'design',
    label: 'Earlier work with us',
    blurb: 'Link this to a prior Aayatan Veda engagement, if any.',
    impl: 'scaffold',
    scaffoldFields: [
      { name: 'prior_engagement', label: 'Prior report / project reference', kind: 'text' },
    ],
  },

  /* ── health ── */
  {
    key: 'health_history',
    group: 'health',
    label: 'Health & wellness',
    blurb: 'Only for wellness-linked services. You decide what to share.',
    impl: 'scaffold',
    scaffoldFields: [
      {
        name: 'health_note',
        label: 'Health concerns you choose to share',
        kind: 'textarea',
        hint: 'Optional. Not a medical record — guidance only.',
      },
    ],
  },

  /* ── staged ── */
  {
    key: 'l2b_r2_followup',
    group: 'staged',
    label: 'Follow-up (Report 2)',
    blurb: 'Captured before the second report.',
    impl: 'scaffold',
    scaffoldFields: [
      { name: 'r2_note', label: 'Update since Report 1', kind: 'textarea' },
    ],
  },

  /* ── ancillary (REQUEST modules) ── */
  {
    key: 'site_visit_request',
    group: 'ancillary',
    label: 'Site visit request',
    blurb: 'Where and when you would like a site visit.',
    impl: 'scaffold',
    scaffoldFields: [
      { name: 'visit_address', label: 'Site address', kind: 'textarea' },
      { name: 'visit_when', label: 'Preferred date(s)', kind: 'text' },
      { name: 'visit_note', label: 'Anything else we should know', kind: 'textarea' },
    ],
  },
  {
    key: 'survey_map_request',
    group: 'ancillary',
    label: 'Survey & map request',
    blurb: 'Details for a measured survey / map.',
    impl: 'scaffold',
    scaffoldFields: [
      { name: 'survey_address', label: 'Site address', kind: 'textarea' },
      { name: 'survey_area', label: 'Approx. built-up area (sq ft)', kind: 'text' },
      { name: 'survey_note', label: 'Anything else we should know', kind: 'textarea' },
    ],
  },
  {
    key: 'map_grading_request',
    group: 'ancillary',
    label: 'Map grading request',
    blurb: 'Upload the plan you want graded (grid making).',
    impl: 'scaffold',
    scaffoldFields: [
      { name: 'grading_note', label: 'Note on the plan to be graded', kind: 'textarea' },
      { name: 'grading_north', label: 'Do you know the north direction?', kind: 'text' },
    ],
  },
];

/* ───────────────────────────── lookups ───────────────────────────── */

const BY_KEY: Record<string, ModuleDef> = Object.fromEntries(
  MODULE_LIBRARY.map((m) => [m.key, m]),
);

/** PURE — resolve a module descriptor by key, or undefined if unknown. */
export function moduleByKey(key: string): ModuleDef | undefined {
  return BY_KEY[key];
}

/** PURE — every module key the library knows. */
export function allModuleKeys(): string[] {
  return MODULE_LIBRARY.map((m) => m.key);
}

/** PURE — is this module a real (answers-wired) implementation? */
export function isRealModule(key: string): boolean {
  return BY_KEY[key]?.impl === 'real';
}

/**
 * PURE — run the validators for the MANDATORY modules of a selection. A module
 * only blocks when it is flagged 'mandatory' AND has a validator. Returns the
 * combined human messages ([] == ok to save the stage).
 */
export function validateModules(
  moduleKeys: string[],
  flags: Record<string, ModuleFlag>,
  form: IntakeForm,
): string[] {
  const out: string[] = [];
  for (const key of moduleKeys) {
    const def = BY_KEY[key];
    if (!def || !def.validate) continue;
    if (flags[key] === 'mandatory') out.push(...def.validate(form));
  }
  // de-dupe identical messages (multiple mandatory modules may share validateIntakeForm)
  return Array.from(new Set(out));
}
